import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../context/AuthContext';
import { useEntitlements } from '../context/EntitlementsContext';
import AppHeader from '../components/AppHeader';
import MaintenancePanel from '../components/MaintenancePanel';
import { Plus, Copy, Trash, CheckCircle, Lock, ArrowsClockwise, FlaskIcon, ArrowRight } from '@phosphor-icons/react';

export default function Admin() {
    const { user } = useAuth();
    const ent = useEntitlements();
    const navigate = useNavigate();
    const [invites, setInvites] = useState([]);
    const [counts, setCounts] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [note, setNote] = useState('');
    const [filter, setFilter] = useState('all');

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [a, b] = await Promise.all([
                axios.get(`${API}/admin/invites${filter === 'all' ? '' : `?status_filter=${filter}`}`, { withCredentials: true }),
                axios.get(`${API}/admin/users/summary`, { withCredentials: true }),
            ]);
            setInvites(a.data.invites);
            setCounts(a.data.counts);
            setSummary(b.data);
        } catch (err) {
            if (err?.response?.status === 403) {
                // non-admin — bounce out
                navigate('/app');
                return;
            }
            toast.error('Could not load admin data.');
        } finally {
            setLoading(false);
        }
    }, [filter, navigate]);

    useEffect(() => { if (user) loadAll(); }, [user, loadAll]);

    if (!user) return null;
    if (ent?.ready && !ent?.isAdmin) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-white">
                <div className="text-center border border-[#18181B] p-6">
                    <Lock size={32} weight="duotone" color="#52525B" className="mx-auto" />
                    <h1 className="font-display text-xl font-bold mt-2">Admin only</h1>
                    <p className="text-sm text-[#52525B] mt-1">You don&apos;t have access to this page.</p>
                    <button onClick={() => navigate('/app')} className="mt-4 underline decoration-[#1FA5CF] underline-offset-4 font-mono text-xs uppercase tracking-[0.2em]">
                        Back to DefectR
                    </button>
                </div>
            </div>
        );
    }

    const createInvite = async () => {
        try {
            const res = await axios.post(`${API}/admin/invites`, { note }, { withCredentials: true });
            setNote('');
            toast.success(`Created ${res.data.invite.code}`);
            await loadAll();
        } catch {
            toast.error('Could not create invite.');
        }
    };

    const revokeInvite = async (code) => {
        if (!window.confirm(`Revoke invite ${code}? This also downgrades any user who claimed it.`)) return;
        try {
            await axios.patch(`${API}/admin/invites/${code}`, { status: 'revoked' }, { withCredentials: true });
            toast.success('Invite revoked');
            await loadAll();
        } catch {
            toast.error('Could not revoke.');
        }
    };

    const copyInviteLink = (code) => {
        const url = `${window.location.origin}/invite/${code}`;
        navigator.clipboard.writeText(url).then(() => toast.success('Link copied to clipboard')).catch(() => toast.error('Could not copy'));
    };

    return (
        <div className="min-h-screen bg-white pb-10">
            <AppHeader title="Admin" />
            <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-8" data-testid="admin-page">
                <StatsStrip counts={counts} summary={summary} />

                {/* Godmode — admin QA shortcut to live-test the Developer
                    tier without paying Stripe. Reversible via the same
                    panel. Hard-gated by the backend's require_admin
                    check, so even if someone bypassed the frontend, the
                    endpoint refuses non-admin callers. */}
                <DeveloperGodmodePanel onChange={loadAll} />
                <MaintenancePanel />
                <EmailPreviewPanel />

                {/* Create invite */}
                <section className="border border-[#18181B]">
                    <div className="px-4 py-3 border-b border-[#18181B] flex items-center justify-between">
                        <h2 className="font-display font-semibold">Issue Architect invite</h2>
                        <button
                            type="button"
                            onClick={loadAll}
                            data-testid="admin-refresh-btn"
                            className="p-1.5 hover:bg-[#F4F4F5] text-[#52525B]"
                            title="Refresh"
                        >
                            <ArrowsClockwise size={16} weight="bold" />
                        </button>
                    </div>
                    <div className="p-4 flex items-center gap-3">
                        <input
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Note (optional) — e.g. 'For Dad'"
                            data-testid="admin-invite-note-input"
                            className="flex-1 border border-[#18181B] px-3 py-2 text-sm"
                            maxLength={120}
                        />
                        <button
                            type="button"
                            onClick={createInvite}
                            data-testid="admin-create-invite-btn"
                            className="bg-[#1FA5CF] text-white px-4 py-2 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#1787AB] inline-flex items-center gap-1.5 active:scale-95 transition-transform"
                        >
                            <Plus size={14} weight="bold" /> Generate
                        </button>
                    </div>
                </section>

                {/* Invites table */}
                <section className="border border-[#18181B]">
                    <div className="px-4 py-3 border-b border-[#18181B] flex items-center justify-between">
                        <h2 className="font-display font-semibold">Architect invites</h2>
                        <div className="flex items-center gap-1 text-xs">
                            {['all', 'pending', 'claimed', 'revoked'].map(f => (
                                <button
                                    key={f}
                                    type="button"
                                    onClick={() => setFilter(f)}
                                    data-testid={`admin-filter-${f}`}
                                    className={`px-2 py-1 font-mono uppercase tracking-[0.18em] ${filter === f ? 'bg-[#18181B] text-white' : 'hover:bg-[#F4F4F5]'}`}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>
                    {loading ? (
                        <div className="p-8 text-center text-[#52525B] text-sm">Loading…</div>
                    ) : invites.length === 0 ? (
                        <div className="p-8 text-center text-[#52525B] text-sm">No invites yet.</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="text-left label-tiny bg-[#FAFAFA]">
                                    <tr>
                                        <th className="px-4 py-2">Code</th>
                                        <th className="px-4 py-2">Note</th>
                                        <th className="px-4 py-2">Status</th>
                                        <th className="px-4 py-2">Claimed by</th>
                                        <th className="px-4 py-2">Created</th>
                                        <th className="px-4 py-2 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invites.map(inv => (
                                        <tr key={inv.code} className="border-t border-[#E4E4E7]" data-testid={`admin-invite-row-${inv.code}`}>
                                            <td className="px-4 py-2 font-mono">{inv.code}</td>
                                            <td className="px-4 py-2 text-[#52525B]">{inv.note || '—'}</td>
                                            <td className="px-4 py-2"><StatusPill status={inv.status} /></td>
                                            <td className="px-4 py-2 text-[#52525B]">{inv.claimed_by_email || '—'}</td>
                                            <td className="px-4 py-2 text-[#52525B] text-xs">{(inv.created_at || '').slice(0, 10)}</td>
                                            <td className="px-4 py-2 text-right">
                                                <div className="inline-flex items-center gap-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => copyInviteLink(inv.code)}
                                                        data-testid={`admin-copy-${inv.code}`}
                                                        title="Copy invite link"
                                                        className="p-1.5 hover:bg-[#F4F4F5]"
                                                    >
                                                        <Copy size={14} />
                                                    </button>
                                                    {inv.status === 'pending' || inv.status === 'claimed' ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => revokeInvite(inv.code)}
                                                            data-testid={`admin-revoke-${inv.code}`}
                                                            title={inv.status === 'claimed' ? 'Revoke (downgrades user)' : 'Revoke'}
                                                            className="p-1.5 hover:bg-[#FEE2E2] text-[#C0392B]"
                                                        >
                                                            <Trash size={14} />
                                                        </button>
                                                    ) : null}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
}

function StatsStrip({ counts, summary }) {
    if (!counts || !summary) return null;
    const cells = [
        ['Foundation', summary.users.foundation],
        ['Rectify', summary.users.rectify],
        ['Surveyor', summary.users.surveyor],
        ['Architect', summary.users.architect],
        ['Properties', summary.properties_total],
        ['Paid', summary.payments_completed],
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-6 gap-0 border border-[#18181B]" data-testid="admin-summary">
            {cells.map(([label, value], idx) => (
                <div key={label} className={`p-4 ${idx < cells.length - 1 ? 'sm:border-r border-[#18181B]' : ''}`}>
                    <div className="label-tiny">{label}</div>
                    <div className="font-display text-2xl font-bold mt-1">{value}</div>
                </div>
            ))}
        </div>
    );
}

function StatusPill({ status }) {
    const tone = status === 'pending' ? 'bg-[#E8F5FB] text-[#1787AB]' :
                 status === 'claimed' ? 'bg-[#E8F7EE] text-[#00875A]' :
                 'bg-[#FEE2E2] text-[#C0392B]';
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono uppercase tracking-[0.16em] ${tone}`}>
            {status === 'claimed' && <CheckCircle size={10} weight="fill" />}
            {status}
        </span>
    );
}


/**
 * Admin-only "Test as Developer" panel.
 *
 * Lets the platform admin grant themselves a fully-functional Developer
 * tier team without paying — for QA and demo purposes. The granted team
 * is stamped with `stripe_status='test_godmode'` so it's distinguishable
 * from a real subscription in Mongo, and the revoke endpoint refuses to
 * tear down anything that ISN'T a godmode team (so we can't accidentally
 * delete a real customer's team via this UI).
 *
 * On grant the admin's tier flips foundation/architect → developer_team.
 * The admin's `previous_tier` is stored on the team doc so revoke
 * restores them to exactly where they were before (typically architect).
 */
function DeveloperGodmodePanel({ onChange }) {
    const [team, setTeam] = useState(null);
    const [busy, setBusy] = useState(false);

    const loadCurrentTeam = useCallbackHook(() => {
        axios
            .get(`${API}/b2b/team`, { withCredentials: true })
            .then((res) => setTeam(res.data?.team || null))
            .catch(() => setTeam(null));
    });

    useEffect(() => { loadCurrentTeam(); }, [loadCurrentTeam]);

    const isGodmode = team?.stripe_status === 'test_godmode';
    const hasRealTeam = !!team && !isGodmode;

    const grant = async () => {
        setBusy(true);
        try {
            const res = await axios.post(`${API}/b2b/admin/test/grant`, {}, { withCredentials: true });
            if (res.data?.already_existed) {
                toast.success('You already have a test team — opening dashboard.');
            } else {
                toast.success('🧪 Test team granted. You are now on the Developer tier.');
            }
            // Tell parent to refresh + bounce to /team for the user
            // to actually QA the experience.
            onChange?.();
            setTimeout(() => { window.location.href = '/team'; }, 800);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not grant test team.');
        } finally {
            setBusy(false);
        }
    };

    const revoke = async () => {
        if (!window.confirm('Tear down your test team and restore your previous tier?')) return;
        setBusy(true);
        try {
            const res = await axios.post(`${API}/b2b/admin/test/revoke`, {}, { withCredentials: true });
            toast.success(`Test team removed · restored to ${res.data?.restored_tier || 'architect'}.`);
            setTeam(null);
            onChange?.();
            // Force a hard reload so the tier-driven UI (badges,
            // upgrade modal, /team gate) re-evaluates against the
            // restored tier rather than the cached developer_team one.
            setTimeout(() => window.location.reload(), 800);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not revoke test team.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <section className="border-2 border-dashed border-[#7C3AED] bg-[#FAF5FF]" data-testid="admin-godmode-panel">
            <div className="px-4 py-3 border-b border-[#E9D5FF] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FlaskIcon size={18} weight="duotone" color="#7C3AED" />
                    <h2 className="font-display font-semibold">Test as Developer (godmode)</h2>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#7C3AED]">
                    Admin · QA-only
                </span>
            </div>
            <div className="p-4 text-sm">
                {hasRealTeam ? (
                    <div>
                        <p className="text-[#52525B]">
                            You&apos;re on a <strong>real</strong> {team?.tier} team
                            (<code className="bg-white px-1">{team?.stripe_status}</code>). Godmode is disabled —
                            cancel via the Stripe portal first if you want to test the unpaid flow.
                        </p>
                    </div>
                ) : isGodmode ? (
                    <div className="space-y-3">
                        <div className="flex items-center gap-2 text-[#7C3AED] font-mono text-xs uppercase tracking-[0.18em]">
                            <CheckCircle size={14} weight="fill" /> Test team active
                        </div>
                        <p className="text-[#52525B]">
                            <strong>{team?.name}</strong> · {team?.seat_used}/{team?.seat_cap} seats ·
                            active until {team?.active_until ? new Date(team.active_until).toLocaleDateString('en-GB') : '—'}.
                            Visit <a href="/team" className="underline">/team</a> to QA the dashboard, or revoke to restore.
                        </p>
                        <div className="flex gap-2 flex-wrap">
                            <a
                                href="/team"
                                data-testid="admin-open-test-team"
                                className="inline-flex items-center gap-1 bg-[#7C3AED] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#5B21B6] active:scale-95 transition-all"
                            >
                                Open team dashboard <ArrowRight size={12} weight="bold" />
                            </a>
                            <button
                                type="button"
                                onClick={revoke}
                                disabled={busy}
                                data-testid="admin-revoke-test-team"
                                className="inline-flex items-center gap-1 border border-[#7C3AED] text-[#7C3AED] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#7C3AED] hover:text-white active:scale-95 transition-all disabled:opacity-60"
                            >
                                <Trash size={12} weight="bold" /> Revoke test team
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-[#52525B]">
                            One-click grant yourself a fully-functional Developer-tier team without paying. Lasts 90 days, fully reversible.
                            Stamped <code className="bg-white px-1">stripe_status=test_godmode</code> in Mongo so it never gets confused with a real customer.
                        </p>
                        <button
                            type="button"
                            onClick={grant}
                            disabled={busy}
                            data-testid="admin-grant-test-team"
                            className="inline-flex items-center gap-1 bg-[#7C3AED] text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#5B21B6] active:scale-95 transition-all disabled:opacity-60"
                        >
                            <Plus size={12} weight="bold" /> Grant me a test Developer team
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}

/** Tiny local wrapper so the panel can use useCallback without
 *  pulling the import order out of shape; React's useCallback is
 *  already imported at the top of the file. */
function useCallbackHook(fn) {
    return useCallback(fn, [fn]);
}

/**
 * Resend HTML template preview — admin-only panel to fire a test email
 * to the admin's own inbox so they can eyeball each template BEFORE the
 * first real customer ever sees one. Backend endpoint is hard-gated to
 * the admin's email so this can never accidentally spam a real user.
 */
function EmailPreviewPanel() {
    const [busy, setBusy] = useState(null);   // 'team_invite' | 'builder_digest' | null
    const send = async (template) => {
        setBusy(template);
        try {
            const res = await axios.post(
                `${API}/admin/email/preview`,
                { template },
                { withCredentials: true },
            );
            toast.success(`Sent ${template.replace('_', ' ')} preview → ${res.data?.sent_to}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Preview send failed.');
        } finally {
            setBusy(null);
        }
    };
    return (
        <section className="border-2 border-dashed border-[#1FA5CF] bg-[#F0FBFD]" data-testid="admin-email-preview-panel">
            <div className="px-4 py-3 border-b border-[#BAE6FD] flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <ArrowRight size={18} weight="duotone" color="#1FA5CF" />
                    <h2 className="font-display font-semibold">Resend template preview</h2>
                </div>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1787AB]">
                    Admin · QA-only
                </span>
            </div>
            <div className="p-4 text-sm space-y-3">
                <p className="text-[#52525B]">
                    Sends a one-off test email to your inbox so you can eyeball each template before a real customer sees it.
                    Both emails are dispatched only to you — never to any other user.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button
                        type="button"
                        onClick={() => send('team_invite')}
                        disabled={busy !== null}
                        data-testid="email-preview-team-invite"
                        className="inline-flex items-center gap-1 bg-[#1FA5CF] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all disabled:opacity-60"
                    >
                        {busy === 'team_invite' ? 'Sending…' : 'Send team-invite preview'}
                    </button>
                    <button
                        type="button"
                        onClick={() => send('builder_digest')}
                        disabled={busy !== null}
                        data-testid="email-preview-builder-digest"
                        className="inline-flex items-center gap-1 bg-[#1FA5CF] text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] hover:bg-[#1787AB] active:scale-95 transition-all disabled:opacity-60"
                    >
                        {busy === 'builder_digest' ? 'Sending…' : 'Send builder-digest preview'}
                    </button>
                </div>
            </div>
        </section>
    );
}

